import _ from 'lodash';
import moment from 'moment';
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';

// Material UI
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
//import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';

//- Components
import FilterHeader from '../../components/table/FilterHeader';
import EnhancedTableHead from '../../components/table/EnhancedTableHead';

//- Helpers
import { isFiltered, sortTable, PureRecordRow } from '../../libs/tableUtilities';
import { getGlobalClasses } from '../../themes';

//- Containers
import AuthUserContainer from '../../containers/AuthUser';
import TimeClockPermissionContainer from '../../containers/TimeClock/TimeClockPermission';

const styles = (theme) => {
	return {
		...getGlobalClasses(theme),
		table: {
			width: '100%',
			tableLayout: 'auto',
		},
	};
};

const timeClockPermissionColumns = [
	{
		id: 'supervisorDisplay',
		label: 'Supervisor',
	},
	{
		id: 'canViewDisplay',
		label: 'Permission to View',
	},
	{
		id: 'canModifyDisplay',
		label: 'Permission to Modify',
	},
	{
		id: 'dateCreated',
		label: 'Date Created',
	},
	{
		id: 'edit',
		label: 'Edit',
		style: { textAlign: 'center' },
		isNotSortable: true,
	},
];

class TimeClockPermissions extends Component {
	constructor(props) {
		super(props);

		this.state = {
			order: 'asc',
			orderBy: 'displayName',
			filter: '',
			tabIndex: 0,
			filterMap: {},
		};
	}

	componentDidMount() {
		if (_.isUndefined(this.props.timeClockPermissions)) {
			this.props.searchTimeClockPermissions();
		}
	}

	handleSort = (orderBy) => {
		let order = 'desc';
		if (this.state.orderBy === orderBy && this.state.order === 'desc') {
			order = 'asc';
		}
		this.setState({ order, orderBy });
	};

	handleEdit = (timeClockPermissionId) => {
		this.props.history.push(`/timeClockPermission/update/${timeClockPermissionId}`);
	};

	handleFilter = (event) => {
		this.setState({ filter: event.target.value });
	};

	getUserDisplay = (userId) => {
		const { userMap } = this.props;
		const user = userMap[userId];
		if (user) {
			return `${user.firstName} ${user.lastName}`;
		} else {
			return 'Missing User';
		}
	};

	getLocationDisplay = (locationId) => {
		const { locationMap } = this.props;
		const location = locationMap[locationId];
		if (location) {
			return `${location.display}`;
		} else {
			return 'Missing Location';
		}
	};

	renderRuleDisplay = ({ rules, display }) => {
		if (_.isEmpty(rules)) {
			return `No ${display} permissions`;
		}

		if (rules.length > 5) {
			return `${rules.length} x ${display} permissions`;
		}

		return (
			<Fragment>
				{rules.map((rule, i) => {
					const { forEveryone, forLocationId, forUserId } = rule;
					let display = 'empty';
					if (forEveryone) {
						display = 'Everyone';
					} else if (forUserId) {
						display = `User : ${this.getUserDisplay(forUserId)}`;
					} else if (forLocationId) {
						display = `Location : ${this.getLocationDisplay(forLocationId)}`;
					}
					return <div key={i}>{display}</div>;
				})}
			</Fragment>
		);
	};

	render() {
		const {
			classes,
			timeClockPermissions,
			permissions,
			//userMap,
			//locationMap,
		} = this.props;

		const { order, orderBy, filter } = this.state;

		if (!permissions || !_.includes(permissions, 'canUpdateTimeClockPermissions')) {
			return <div>You do not have valid permissions to view this page.</div>;
		}
		if (_.isUndefined(timeClockPermissions)) {
			return <div>Fetching supervisor permissions ...</div>;
		}

		let timeClockPermissionRows = timeClockPermissions.reduce((result, timeClockPermission) => {
			const {
				timeClockPermissionId,
				supervisorUserId,
				canViewRules,
				canModifyRules,
				dateCreated,
			} = timeClockPermission;

			const dateCreatedFormatted = moment(dateCreated).format('MMM Do, YYYY');

			const supervisorDisplay = this.getUserDisplay(supervisorUserId);

			/** @namespace byPermissionType.canViewTimeClock **/
			/** @namespace byPermissionType.canModifyTimeClock **/
			let canViewDisplay = this.renderRuleDisplay({ rules: canViewRules, display: 'view' });
			let canModifyDisplay = this.renderRuleDisplay({ rules: canModifyRules, display: 'modify' });

			const row = {
				timeClockPermissionId,
				supervisorUserId,
				supervisorDisplay,
				dateCreatedFormatted,
				canViewDisplay,
				canModifyDisplay,
				dateCreated,
				onClick: () => this.handleEdit(timeClockPermissionId),
			};

			if (isFiltered({ filter, row, fields: ['supervisorDisplay'] })) {
				return result;
			}

			result.push(row);
			return result;
		}, []);

		timeClockPermissionRows = sortTable({ rows: timeClockPermissionRows, order, orderBy });

		return (
			<Fragment>
				<FilterHeader
					value={this.state.filter}
					onChange={this.handleFilter}
					label={'Supervisor Permissions'}
					buttonText="Add Permission"
					url="/timeClockPermission/add"
					canMakeUpdates={true}
				/>

				<Paper className={classes.globalMainPaper}>
					<Table size="small" className={classes.table}>
						<EnhancedTableHead
							columns={timeClockPermissionColumns}
							order={order}
							orderBy={orderBy}
							onRequestSort={this.handleSort}
						/>
						<TableBody>
							{timeClockPermissionRows.map((row) => {
								return (
									<PureRecordRow hover className={classes.row} key={row.supervisorUserId}>
										<TableCell>{row.supervisorDisplay}</TableCell>
										<TableCell>{row.canViewDisplay}</TableCell>
										<TableCell>{row.canModifyDisplay}</TableCell>
										<TableCell>{row.dateCreatedFormatted}</TableCell>
										<TableCell align={'center'} style={{ whiteSpace: 'nowrap' }}>
											<Button onClick={row.onClick}>Edit</Button>
										</TableCell>
									</PureRecordRow>
								);
							})}
						</TableBody>
					</Table>
				</Paper>
			</Fragment>
		);
	}
}

TimeClockPermissions.propTypes = {
	classes: PropTypes.object.isRequired,

	searchTimeClockPermissions: PropTypes.func.isRequired,
	timeClockPermissions: PropTypes.array,
	userMap: PropTypes.object,
	locationMap: PropTypes.object,

	permissions: PropTypes.array,
	order: PropTypes.string,
	orderBy: PropTypes.string,
	history: PropTypes.object,
	theme: PropTypes.object.isRequired,
};

export default _.flow([
	withStyles(styles, { withTheme: true }),
	AuthUserContainer,
	TimeClockPermissionContainer,
])(TimeClockPermissions);
